import { useState } from 'react';
import { openSignedInTap } from '../../utils/openDocument';

/**
 * LOI-PDF1b (the 130th). ONE "Open PDF" for the screens that gained it - the Documents library and the Deal Room.
 *
 * A filed letter's real PDF (LOI-PDF1) is made on the server and reached through a signed link. The button owns the
 * part every screen must get the same:
 *
 * ⛔ THE WINDOW OPENS INSIDE THE TAP (MOB4, measured on his phone): `openSignedInTap` opens it before anything is
 *   awaited and points it at the link when it arrives. `sign` is handed in as a function precisely so a caller cannot
 *   await it on the way here - a link signed first and opened after is a dead button on an iPhone at its default.
 * ⛔ A PDF THAT CANNOT BE MADE never leaves a blank window: `openSignedInTap` has closed it by the time `onFailed` is
 *   called, with the server's own words (no PDF for this document, no converter on this server). What he is left with
 *   is the SCREEN's to decide - the Deal Room opens the Word file in its viewer, the library points at "Word file".
 * ⭐ The first open of a letter MAKES the PDF, a few seconds - so the button says it is working and cannot be pressed
 *   twice meanwhile.
 */
interface Props {
  /** Asks the server for the PDF's signed link. Called once per press, inside the tap. */
  sign: () => Promise<string>;
  /** The press has begun - a screen clears its stale message here. */
  onStart?: () => void;
  /** The PDF was refused or could not be made. The window is already closed. */
  onFailed: (message: string) => void;
  className?: string;
}

const LOOK =
  'rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50';

export default function OpenPdfButton({ sign, onStart, onFailed, className }: Props) {
  const [opening, setOpening] = useState(false);

  const open = async () => {
    setOpening(true);
    onStart?.();
    try {
      await openSignedInTap(sign);
    } catch (e) {
      onFailed(e instanceof Error && e.message ? e.message : 'The PDF could not be made.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <button type="button" onClick={() => void open()} disabled={opening} className={className ?? LOOK}>
      {opening ? 'Opening PDF...' : 'Open PDF'}
    </button>
  );
}
